import React, { ReactNode, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import { changePasswordInitialValues, changePasswordSchema } from 'constants/schemaValidations';
import useTranslate from 'hooks/useTranslate';
import { ChangePasswordInterface } from 'constants/interfaces';
import { useChangePasswordMutation } from 'services';
import toast from 'react-hot-toast';
import Loader from 'components/Common/Loader';
import './styles.scss';
import { useNavigate } from 'react-router-dom';
import useAuthStore from 'store/auth';

const ChangePassword: React.FC = () => {
	const { translate } = useTranslate();
	const navigate = useNavigate();
	const { mutateAsync: changePassword, isPending } = useChangePasswordMutation();
	const { user }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});

	const [showPassword, setShowPassword] = useState({
		currentPassword: false,
		newPassword: false,
		confirmPassword: false,
	});

	useEffect(() => {
		if (user?.signUpType !== 'normal') return navigate('/');
	}, [user?.userId]);

	const togglePasswordVisibility = (
		field: 'currentPassword' | 'newPassword' | 'confirmPassword',
	) => {
		setShowPassword((prev) => ({
			...prev,
			[field]: !prev[field],
		}));
	};

	const formik: any = useFormik({
		initialValues: changePasswordInitialValues,
		validationSchema: changePasswordSchema(translate),
		onSubmit: async (values) => {
			changePassword({ ...values }).then((res: any) => {
				if (res?.success) {
					toast.success(res?.message);
					formik.resetForm();
				}
			});
		},
	});

	const renderError = <T extends keyof ChangePasswordInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<div className="white-box bg-white">
			<div className="change-password-form w-100">
				<div className="text-center mb-4 mb-md-5">
					<h3 className="fw-bold">{translate('Common.changePassword')}</h3>
				</div>
				<Form className="form" onSubmit={formik.handleSubmit}>
					<Form.Group className="mb-4 position-relative">
						<Form.Label className="lh-1">{translate('PlaceHolder.oldPassword')}*</Form.Label>
						<Form.Control
							type={showPassword.currentPassword ? 'text' : 'password'}
							name="currentPassword"
							placeholder={translate('PlaceHolder.oldPassword')}
							className="rounded-pill"
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							value={formik.values.currentPassword}
							autoComplete="off"
						/>
						<i
							className={`ri-eye${!showPassword.currentPassword ? '-off' : ''}-line form-icon position-absolute lh-1`}
							onClick={() => togglePasswordVisibility('currentPassword')}
						></i>
						{renderError('currentPassword')}
					</Form.Group>

					<Form.Group className="mb-4 position-relative">
						<Form.Label className="lh-1">{translate('PlaceHolder.newPassword')}*</Form.Label>
						<Form.Control
							type={showPassword.newPassword ? 'text' : 'password'}
							name="newPassword"
							placeholder={translate('PlaceHolder.newPassword')}
							className="rounded-pill"
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							value={formik.values.newPassword}
							autoComplete="off"
						/>
						<i
							className={`ri-eye${!showPassword.newPassword ? '-off' : ''}-line form-icon position-absolute lh-1`}
							onClick={() => togglePasswordVisibility('newPassword')}
						></i>
						{renderError('newPassword')}
					</Form.Group>

					<Form.Group className="mb-4 position-relative">
						<Form.Label className="lh-1">{translate('PlaceHolder.confirmPassword')}</Form.Label>
						<Form.Control
							type={showPassword.confirmPassword ? 'text' : 'password'}
							name="confirmPassword"
							placeholder={translate('PlaceHolder.confirmPassword')}
							className="rounded-pill"
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							value={formik.values.confirmPassword}
							autoComplete="off"
						/>
						<i
							className={`ri-eye${!showPassword.confirmPassword ? '-off' : ''}-line form-icon position-absolute lh-1`}
							onClick={() => togglePasswordVisibility('confirmPassword')}
						></i>
						{renderError('confirmPassword')}
					</Form.Group>

					<div className="form-btn">
						{isPending ? (
							<Loader />
						) : (
							<Button className="filled-btn rounded-pill w-100" type="submit">
								{translate('Common.savePassword')}
							</Button>
						)}
					</div>
				</Form>
			</div>
		</div>
	);
};

export default ChangePassword;
